import { useState } from 'react'
import { Zap, Target, AlertTriangle, BarChart3, Shield, Clock, Play, Lock } from 'lucide-react'

export default function PenetrationTestPage() {
  const [stats] = useState({
    testsConducted: 34,
    vulnerabilitiesFound: 23,
    criticalIssues: 2,
    riskScore: '7.2/10',
  })

  const pentestTypes = [
    {
      name: 'Web Application Pentest',
      description: 'Simulated cyberattack against web apps and APIs',
      scope: ['SQL Injection', 'XSS', 'CSRF', 'Authentication Bypass', 'API Abuse'],
      duration: '2-3 weeks',
      color: 'from-red-600 to-red-500',
      icon: Target,
    },
    {
      name: 'Infrastructure Pentest',
      description: 'Test network, servers, and system security',
      scope: ['Port Scanning', 'Service Enumeration', 'Privilege Escalation', 'Network Pivoting', 'Data Exfiltration'],
      duration: '3-4 weeks',
      color: 'from-orange-600 to-orange-500',
      icon: Shield,
    },
    {
      name: 'Social Engineering',
      description: 'Test employee awareness and security practices',
      scope: ['Phishing Campaigns', 'Pretexting', 'Physical Security', 'Credential Harvesting', 'Awareness Assessment'],
      duration: '1-2 weeks',
      color: 'from-yellow-600 to-yellow-500',
      icon: AlertTriangle,
    },
    {
      name: 'Cloud Infrastructure Pentest',
      description: 'AWS, Azure, GCP configuration and security testing',
      scope: ['IAM Misconfigurations', 'Storage Exposure', 'Networking Issues', 'Backup Security', 'API Security'],
      duration: '2-3 weeks',
      color: 'from-blue-600 to-blue-500',
      icon: Zap,
    },
    {
      name: 'API Security Testing',
      description: 'REST, GraphQL, and gRPC API vulnerability testing',
      scope: ['Authentication Bypass', 'Authorization Flaws', 'Injection Attacks', 'Rate Limiting', 'Data Exposure'],
      duration: '2 weeks',
      color: 'from-purple-600 to-purple-500',
      icon: Lock,
    },
    {
      name: 'Mobile App Pentest',
      description: 'iOS and Android application security testing',
      scope: ['Code Injection', 'Insecure Storage', 'Weak Authentication', 'Certificate Pinning', 'Jailbreak Detection'],
      duration: '2-3 weeks',
      color: 'from-green-600 to-green-500',
      icon: Target,
    },
  ]

  const pentestPhases = [
    { phase: 'Reconnaissance', description: 'Gather information about target systems', icon: '🔍' },
    { phase: 'Scanning & Enumeration', description: 'Identify live hosts, services, and vulnerabilities', icon: '📊' },
    { phase: 'Exploitation', description: 'Attempt to exploit found vulnerabilities', icon: '⚔️' },
    { phase: 'Privilege Escalation', description: 'Escalate access to higher privilege levels', icon: '🔐' },
    { phase: 'Persistence & Lateral Movement', description: 'Maintain access and move across network', icon: '🚀' },
    { phase: 'Data Exfiltration', description: 'Extract sensitive data to demonstrate impact', icon: '📤' },
    { phase: 'Report & Remediation', description: 'Document findings and provide remediation steps', icon: '📋' },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="space-y-6">
        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-red-600 via-orange-600 to-yellow-600 p-8 text-white shadow-2xl border border-red-500/20">
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <Target className="w-8 h-8 text-yellow-300" />
                  <h1 className="text-5xl font-black">Penetration Testing</h1>
                </div>
                <p className="text-red-100 text-lg font-semibold">Simulated Cyberattacks & Security Assessments</p>
                <p className="text-red-200 text-sm mt-1">Web Apps • Infrastructure • Social Engineering • Cloud • APIs • Mobile</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black text-yellow-300">{stats.testsConducted}</div>
                <div className="text-sm text-red-100">Tests Conducted</div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: 'Vulnerabilities Found', value: stats.vulnerabilitiesFound, icon: AlertTriangle, color: 'from-red-600 to-red-500' },
            { label: 'Critical Issues', value: stats.criticalIssues, icon: Shield, color: 'from-orange-600 to-orange-500' },
            { label: 'Risk Score', value: stats.riskScore, icon: BarChart3, color: 'from-yellow-600 to-yellow-500' },
            { label: 'Last Pentest', value: '5 days ago', icon: Clock, color: 'from-purple-600 to-purple-500' },
          ].map((stat, idx) => (
            <div key={idx} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <p className="text-blue-300 text-sm font-semibold">{stat.label}</p>
              <p className="text-white text-3xl font-black mt-3">{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Pentest Types */}
        <div>
          <h2 className="text-3xl font-bold text-white mb-6">Penetration Test Types</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {pentestTypes.map((test, idx) => {
              const Icon = test.icon
              return (
                <div key={idx} className="relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all shadow-lg hover:shadow-blue-500/20">
                  <div className={`h-1 w-full bg-gradient-to-r ${test.color} rounded-full mb-4 shadow-lg`}></div>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-bold text-white text-lg">{test.name}</h3>
                      <p className="text-blue-300 text-sm">{test.description}</p>
                    </div>
                    <div className={`bg-gradient-to-br ${test.color} p-3 rounded-lg`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="mb-4">
                    <p className="text-cyan-400 text-xs font-bold mb-2">Test Scope:</p>
                    <div className="flex flex-wrap gap-2">
                      {test.scope.map((item, sidx) => (
                        <span key={sidx} className="text-xs bg-slate-700/50 text-blue-300 px-2 py-1 rounded border border-blue-500/20">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-blue-500/20">
                    <span className="text-cyan-400 text-sm font-semibold">Duration: {test.duration}</span>
                    <button className={`bg-gradient-to-r ${test.color} hover:shadow-lg transition-all px-4 py-2 rounded-lg text-white text-sm font-bold flex items-center space-x-2`}>
                      <Play className="w-4 h-4" />
                      <span>Start Test</span>
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Pentest Phases */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
          <h3 className="text-2xl font-bold text-white mb-6">Penetration Testing Phases</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-3">
            {pentestPhases.map((item, idx) => (
              <div key={idx} className="bg-slate-700/50 rounded-lg border border-blue-500/20 p-4 text-center hover:border-blue-400/40 transition-all">
                <div className="text-3xl mb-2">{item.icon}</div>
                <p className="text-white font-bold text-sm">{item.phase}</p>
                <p className="text-blue-300 text-xs mt-2">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Attack Vectors & Techniques */}
        <div>
          <h2 className="text-3xl font-bold text-white mb-6">OWASP & Attack Vectors Tested</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { title: 'OWASP Top 10 Web', items: ['Broken Access Control', 'Cryptographic Failures', 'Injection', 'Insecure Design', 'Security Misconfiguration'] },
              { title: 'OWASP Top 10 API', items: ['Broken Object Level Authorization', 'Broken User Authentication', 'Excessive Data Exposure', 'Lack of Resources & Rate Limiting', 'Broken Function Level Auth'] },
              { title: 'Network Attack Vectors', items: ['Man-in-the-Middle', 'DNS Spoofing', 'ARP Poisoning', 'Packet Sniffing', 'Network Segmentation Issues'] },
              { title: 'Exploitation Techniques', items: ['Buffer Overflow', 'Privilege Escalation', 'Lateral Movement', 'Persistence Mechanisms', 'Command Injection'] },
              { title: 'Social Engineering', items: ['Phishing', 'Pretexting', 'Baiting', 'Tailgating', 'Password Cracking'] },
              { title: 'Cloud Security', items: ['IAM Misconfigurations', 'Storage Exposure', 'API Abuse', 'Compute Vulnerabilities', 'Database Issues'] },
            ].map((category, idx) => (
              <div key={idx} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-red-500/20 p-4 hover:border-red-400/40 transition-all">
                <h4 className="font-bold text-red-300 mb-3">{category.title}</h4>
                <ul className="space-y-2">
                  {category.items.map((item, iidx) => (
                    <li key={iidx} className="text-blue-300 text-sm flex items-start space-x-2">
                      <span className="text-red-400 mt-1">▸</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Remediation & Proof of Exploitation */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
          <h3 className="text-xl font-bold text-white mb-4">Remediation & Proof of Exploitation</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h4 className="text-blue-300 font-bold mb-4">Pentest Deliverables</h4>
              <ul className="space-y-3">
                {[
                  'Executive Summary Report',
                  'Detailed Vulnerability Analysis',
                  'Proof of Exploitation (Screenshots/Videos)',
                  'Risk Rating Per Finding',
                  'Remediation Steps & Timeline',
                  'Compliance Impact Assessment',
                  'Security Recommendations',
                  'Retesting Services'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center space-x-3 text-blue-300">
                    <span className="text-green-400">✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-blue-300 font-bold mb-4">Evidence & Documentation</h4>
              <div className="space-y-3">
                {[
                  { label: 'Screenshots', value: '147 images captured' },
                  { label: 'Video Recordings', value: '12 hours of evidence' },
                  { label: 'Network Logs', value: 'Full packet captures' },
                  { label: 'Tool Output', value: 'Burp Suite, Metasploit, etc.' },
                  { label: 'Proof of Access', value: 'Data exfiltration samples' },
                  { label: 'Timeline', value: 'Complete attack chain' },
                ].map((item, idx) => (
                  <div key={idx} className="p-3 bg-slate-700/50 rounded-lg border border-blue-500/20">
                    <p className="text-blue-400 text-sm font-bold">{item.label}</p>
                    <p className="text-cyan-400 text-xs mt-1">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
